import { Component, ErrorInfo, ReactNode } from 'react';
import { isReloadPending } from '../../utils/preloadErrorReload';

interface Props {
  children: ReactNode;
  /**
   * When this changes (the route), a crashed boundary tries its children again — so the back
   * button or a link recovers without a full reload. Leave unset for the outermost boundary.
   */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
}

/**
 * The app-wide error boundary. A render crash anywhere below it — including a lazy route chunk
 * that failed to load after a deploy and wasn't recovered by the `vite:preloadError` reload —
 * shows a friendly "Something went wrong — Reload" screen instead of a white page. Inner
 * boundaries (the admin background-services one) still catch their own errors first.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AppErrorBoundary caught an error:', error, errorInfo.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // A stale-chunk reload is already on its way (see utils/preloadErrorReload): the crash is the
    // cancelled import, and the page is about to be replaced — don't flash an error at them.
    if (isReloadPending()) {
      return (
        <div role="status" className="flex min-h-screen items-center justify-center bg-gray-50 p-6 text-sm text-gray-600 dark:bg-gray-900 dark:text-gray-300">
          Loading the latest version…
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
        <div
          role="alert"
          className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-800 dark:bg-gray-800"
        >
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Something went wrong</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            This page hit a problem. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
